import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthClientConfig, AuthConfig, AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { FwSnackbarModule } from '@flywheel-io/vision';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';

import { environment } from '../environments/environment';
import { AdminService } from './admin/admin.service';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BasicAuthInterceptor } from './auth.interceptor';
import { AuthService } from './auth.service';
import { ChangeManagerService } from './change-manager.service';
import { ConfigService } from './config.service';
import { CoreApiService } from './core-api.service';
import { DataIOErrorInterceptor } from './data-io-error-interceptor.service';
import { DataViewsService } from './data-views/data-views.service';
import { TasksModule as DeprecatedTasksModule } from './deprecated-tasks/tasks.module';
import { DialogModule } from './dialogs/dialog.module';
import { DownloadService } from './download.service';
import { ExhaustiveModeService } from './exhaustive-mode.service';
import { GearModule } from './gears/gear.module';
import { GearService } from './gears/gear.service';
import { LoggingService } from './logging.service';
import { ProfileService } from './profile/profile.service';
import { ProjectsService } from './projects/projects.service';
import { QueriesModule } from './queries/queries.module';
import { LayoutComponent } from './shared/components/layout/layout.component';
import { Auth0Config } from './shared/models/api-config.model';
import { SharedModule } from './shared/shared.module';
import { SidebarService } from './sidebar.service';
import { TaskManagerService } from './task-manager/task-manager.service';
import { DeidLogService } from './upload/deid-log.service';
import { ViewerModule } from './viewers/viewer.module';
import { ViewerService } from './viewers/viewer.service';
import { XferModule } from './xfer/xfer.module';

function parseAuthConfig(configToParse: Auth0Config, configService: ConfigService): AuthConfig {
  const authAudience = configToParse.audience[0]; // use this to override the audience for auth0 debug
  configService.authAudience = authAudience;

  return {
    ...configToParse,
    clientId: configToParse.client_id,
    authorizationParams: {
      audience: authAudience,
      redirect_uri: `${window.location.origin}`,
    },
    cacheLocation: 'localstorage',
    httpInterceptor: {
      allowedList: [
        {
          uri: '/api/*',
        },
        {
          uri: '/io-proxy/*',
        },
        {
          uri: '/ngc/*',
        },
        {
          uri: '/snapshot/*',
        },
        {
          uri: '/xfer/*',
        },
        // the central manager url is added once the api config loads, see ConfigService
      ],
    },
  };
}

function initConfig(authConfig: AuthClientConfig, configService: ConfigService) {
  return async () => {
    // the api config is loaded after auth by the root route's resolver, see app-routing.module
    const auth0Config = await configService.loadAuthConfig();
    if (configService.isAuth0Enabled()) {
      const config: Auth0Config = auth0Config?.auth0 as Auth0Config;
      authConfig.set(parseAuthConfig(config, configService));
    } else {
      // dummy config so auth0 doesn't crash the application
      authConfig.set({
        domain: '',
        clientId: '',
        authorizationParams: {
          audience: '',
          redirect_uri: `${window.location.origin}`,
        },
      });
    }
  };
}
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    // we load the auth config async so we pass nothing here, see initConfig
    AuthModule.forRoot(),
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    // Our modules
    DeprecatedTasksModule,
    DialogModule,
    DeprecatedTasksModule,
    FwSnackbarModule,
    GearModule,
    LayoutComponent,
    QueriesModule,
    SharedModule,
    ViewerModule,
    XferModule,
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = initConfig(inject(AuthClientConfig), inject(ConfigService));
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BasicAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DataIOErrorInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useExisting: LoggingService,
    },
    AdminService,
    AuthService,
    ChangeManagerService,
    ConfigService,
    DataViewsService,
    DeidLogService,
    DownloadService,
    CoreApiService,
    GearService,
    LoggingService,
    ProfileService,
    ProjectsService,
    ExhaustiveModeService,
    SidebarService,
    TaskManagerService,
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60000, // 1 minute
          },
        },
      }),
      withDevtools(() => ({ loadDevtools: environment.tanstack_dev_tools, buttonPosition: 'bottom-left' })),
    ),
    ViewerService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
